import { X } from 'lucide-react';
import Button from '../../shared/Button';
import { useEffect } from 'react';
import { generateReference, generateInText } from '../../../utils/citationGenerator';
import { mapBackendToFormFields, getFrontendSourceType } from '../../../utils/citationFieldMapper';

const styles = {
	section: 'fixed inset-0 z-[9999] flex items-center justify-center p-4',
	backDrop: 'absolute inset-0 bg-black/70',
	modal:
		'relative z-10 w-full max-w-[500px] rounded-[18px] bg-white shadow-2xl p-[30px] max-h-[90vh] overflow-y-auto no-scrollbar',
	heading: 'text-25 font-bold text-dark-blue mb-2 leading-100',
	subHeading: 'text-gray text-sm xl:mb-7 md:mb-2',
	notesSection:
		'bg-lightPurple p-4 rounded-xl text-sm leading-21 text-dark-blue font-medium italic xl:mb-8 mb-3 flex flex-col',
	notesTitle: 'font-semibold text-base leading-100 text-purple xl:mb-2.5 mb-1'
};

export default function CitationDetailModal({ isOpen, onClose, citation, onInsert }) {
	// Inline style for better word breaking of long URLs
	const textBreakStyle = {
		wordBreak: 'break-word',
		overflowWrap: 'anywhere'
	};

	useEffect(() => {
		if (isOpen) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = '';
		}

		return () => {
			document.body.style.overflow = '';
		};
	}, [isOpen]);

	if (!isOpen) return null;

	// Generate proper APA-formatted citations using the same logic as Live Preview
	const category = citation?.category || '';
	const backendSourceType = citation?.subcategory || citation?.sourceType || ''; // Use subcategory (backend) first

	// Convert backend subcategory to frontend source type for citation generator
	const sourceType = getFrontendSourceType(backendSourceType, citation);

	// Transform backend citation data to frontend form fields format
	// The citation generator expects frontend form field names
	const formFields = citation ? mapBackendToFormFields(citation) : {};
	// Generate live citations from transformed citation data
	const fullReference =
		category && sourceType && formFields ? generateReference(category, sourceType, formFields) : '';

	const inTextCitations =
		category && sourceType && formFields
			? generateInText(category, sourceType, formFields)
			: { parenthetical: '', narrative: '' };

	const inTextParenthetical = inTextCitations.parenthetical || '';
	const inTextNarrative = inTextCitations.narrative || '';

	return (
		<div className={styles.section}>
			<div className={styles.backDrop} onClick={onClose} />

			<div className={styles.modal}>
				<button
					onClick={onClose}
					className="absolute top-8 right-2 md:right-6 w-5 h-5 bg-dark-blue/40 hover:bg-dark-blue/80 rounded-full flex items-center justify-center transition-colors"
				>
					<X className="w-3 h-3 text-white" />
				</button>

				<h2 className={styles.heading}>Live Preview</h2>
				<p className={styles.subHeading}>Here is a quick APA citation preview</p>

				<div className="h-[1px] bg-[#F0F0F0] xl:mb-[25px] mb-2 mx-[-30px]" />

				{/* In-text citation examples */}
				<div className="mb-6">
					<h3 className="text-sm font-medium text-dark-blue mb-2">In-text citation</h3>
					<div className="space-y-2">
						{inTextParenthetical && (
							<div className="flex pl-[10px] items-start gap-[10px] self-stretch border-l-[1.5px] border-[#E8E8EA] pr-2">
								<p className="text-sm text-dark-blue/60" style={textBreakStyle}>
									<span className="font-medium">Parenthetical:</span> {inTextParenthetical}
								</p>
							</div>
						)}
						{inTextNarrative && (
							<div className="flex pl-[10px] items-start gap-[10px] self-stretch border-l-[1.5px] border-[#E8E8EA] pr-2">
								<p className="text-sm text-dark-blue/60" style={textBreakStyle}>
									<span className="font-medium">Narrative:</span> {inTextNarrative}
								</p>
							</div>
						)}
					</div>
				</div>

				{/* Full reference */}
				<div className="mb-5">
					<h3 className="text-sm font-medium text-dark-blue mb-2">Full reference</h3>
					<div className="flex pl-[10px] items-start gap-[10px] self-stretch border-l-[1.5px] border-[#E8E8EA] pr-2">
						<p
							className="text-sm text-dark-blue/60 leading-relaxed whitespace-pre-line"
							style={textBreakStyle}
							dangerouslySetInnerHTML={{
								__html: fullReference || 'Citation data not available'
							}}
						/>
					</div>
				</div>

				{citation?.notes && (
					<div className={styles.notesSection}>
						<span className={styles.notesTitle}>Notes:</span>
						{citation.notes}
					</div>
				)}

				<div className="h-[1px] bg-[#F0F0F0] xl:mb-[25px] mb-2 mx-[-30px]" />

				<div className="flex xl:gap-3 gap-2">
					<Button onClick={onInsert}>Insert citation</Button>
				</div>
			</div>
		</div>
	);
}
